import { arrayToMap, arrayToObject, nonNullable } from 'lib/array/arrayUtils'
import {
  DEFAULT_MAP_OPTIONS,
  FIT_BOUNDS_PADDING,
  USER_ZOOM,
} from 'tripPlanner/config/map'
import { MapLayerGroup } from 'tripPlanner/contexts/MapLayers/MapLayersContext'
import {
  ExploreItem,
  FullTrip,
  GeoPlace,
  GeoPoint,
  PlaceGeo,
} from 'tripPlanner/types/common'
import { PublicFullTrip, PublicMapItem, PublicTripItem } from 'tripPlanner/types/publicTrip'
import {
  MapItem,
  MarkerItem,
  RouteItem,
  TripItem,
} from 'tripPlanner/types/tripItem'
import { isExploreItem } from 'tripPlanner/utils/explore'
import { NO_DATES, itinerarySectionToString } from 'tripPlanner/utils/itinerary'
import { isSamePlace } from 'tripPlanner/utils/places'
import { filterProducts } from 'tripPlanner/utils/summary'
import { itemIsInItinerary } from '../Itinerary/utils'
import { getItemPlace } from 'tripPlanner/utils'
import moment from 'moment'

const TARGET_ZOOM = 12

export interface BaseMapElementComponentProps {
  position: GeoPoint
  active: boolean
  zIndex: number
}

interface BaseMapElement {
  position: GeoPoint
  includeInMapBounds: boolean
}

export interface TripItemMapMarker extends BaseMapElement {
  mapElementType: 'tripItemMarker'
  item: MarkerItem
  itemPlaceType: 'start' | 'end' | 'single'
}

export interface TripItemMapRoute extends BaseMapElement {
  mapElementType: 'tripItemRoute'
  item: RouteItem
}

export interface ExploreItemMapMarker extends BaseMapElement {
  mapElementType: 'exploreItemMarker'
  item: ExploreItem
}

export interface LEOfferMapMarker extends BaseMapElement {
  mapElementType: 'leOfferMarker'
  offer: App.HotelOffer | App.ExperienceOffer
}

export type MapElement = TripItemMapMarker | TripItemMapRoute | ExploreItemMapMarker

export const mapToExploreMarker = (
  item: ExploreItem,
): ExploreItemMapMarker => {
  return {
    mapElementType: 'exploreItemMarker',
    position: item.place.point,
    item,
    includeInMapBounds: true,
  }
}

export function isExploreMarker(
  marker: ExploreItemMapMarker | TripItemMapMarker | TripItemMapRoute,
): marker is ExploreItemMapMarker {
  return marker.mapElementType === 'exploreItemMarker'
}

export function isRoute(
  marker: ExploreItemMapMarker | TripItemMapMarker | TripItemMapRoute,
): marker is TripItemMapRoute {
  return marker.mapElementType === 'tripItemRoute'
}

export const mapTripItemToMarker = (
  item: MarkerItem,
  includeInMapBounds: boolean = true,
): TripItemMapMarker | Array<TripItemMapMarker> => {
  if ('place' in item) {
    return {
      mapElementType: 'tripItemMarker',
      position: item.place.point,
      item,
      includeInMapBounds,
      itemPlaceType: 'single',
    }
  }
  if (
    item.endPlace?.type === 'GEO' &&
    !isSamePlace(item.startPlace, item.endPlace)
  ) {
    return [
      {
        mapElementType: 'tripItemMarker',
        position: item.startPlace.point,
        item,
        includeInMapBounds,
        itemPlaceType: 'start',
      },
      {
        mapElementType: 'tripItemMarker',
        position: item.endPlace.point,
        item,
        includeInMapBounds,
        itemPlaceType: 'end',
      },
    ]
  } else {
    return {
      mapElementType: 'tripItemMarker',
      position: item.startPlace.point,
      item,
      includeInMapBounds,
      itemPlaceType: 'single',
    }
  }
}

export const mapTripItemToRoute = (
  item: RouteItem,
  includeInMapBounds: boolean = true,
): TripItemMapRoute => {
  return {
    mapElementType: 'tripItemRoute',
    item,
    position: item.startPlace.point,
    includeInMapBounds,
  }
}

export const mapLEOfferToMarker = (
  offer: App.HotelOffer | App.ExperienceOffer,
): LEOfferMapMarker => {
  return {
    mapElementType: 'leOfferMarker',
    offer,
    position: offer.type === 'experience' ? {
      lat: offer.location.latitude,
      lng: offer.location.longitude,
    } : {
      lat: offer.property.latitude,
      lng: offer.property.longitude,
    },
    includeInMapBounds: true,
  }
}

export const extendMapBounds = (
  markers: Array<TripItemMapMarker | TripItemMapRoute | ExploreItemMapMarker>,
  map?: google.maps.Map,
) => {
  if (!map) {
    return
  }

  const currentBounds = map?.getBounds()

  if (!currentBounds) {
    return
  }

  // An arbitrary zoom value where the user is shown the other
  // markers in the same general region.
  // This is currently disabled as per TRP-2326 title but it'll highly probable this functionality
  // might be re-enabled in the future.
  // const currentZoom = map.getZoom()
  // if (currentZoom && currentZoom > SUMMARY_ZOOM_LEVEL) { // e.g. SUMMARY_ZOOM_LEVEL = 13
  //   map.setZoom(SUMMARY_ZOOM_LEVEL)
  // }

  const boundsToInclude = new google.maps.LatLngBounds(currentBounds)

  for (const marker of markers) {
    if (!marker.includeInMapBounds) {
      return
    }

    boundsToInclude.extend(
      new google.maps.LatLng(marker.position.lat, marker.position.lng),
    )

    if (!isExploreItem(marker.item) && isRouteItem(marker.item)) {
      boundsToInclude.extend(
        new google.maps.LatLng(
          marker.item.endPlace.point.lat,
          marker.item.endPlace.point.lng,
        ),
      )
    }
  }

  // Supplying 0 here to make a fitBounds idempotent on the result of getBounds
  // essentially does not extend bounds if it does not need to.
  map.fitBounds(boundsToInclude, 0)
}

export const setMapBounds = (
  markers: Array<TripItemMapMarker | TripItemMapRoute | ExploreItemMapMarker>,
  map?: google.maps.Map,
  selectedPlace?: PlaceGeo,
  isInitialLoad?: boolean,
) => {
  if (map && markers.length > 0) {
    if (isInitialLoad) {
      const accommodationMarkers = markers.filter(marker =>
        !isExploreItem(marker.item) && marker.item.type === 'ACCOMMODATION',
      )

      if (accommodationMarkers.length === 1) {
        const firstAccommodation = accommodationMarkers[0]
        map.setCenter(new google.maps.LatLng(
          firstAccommodation.position.lat,
          firstAccommodation.position.lng,
        ))
        map.setZoom(TARGET_ZOOM)

        google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
          map.setOptions({ minZoom: USER_ZOOM.min, maxZoom: USER_ZOOM.max })
        })
        return
      }
    }

    const bounds = new google.maps.LatLngBounds()
    nonNullable(markers).forEach((marker) => {
      if (!marker.includeInMapBounds) {
        return
      }

      bounds.extend(
        new google.maps.LatLng(marker.position.lat, marker.position.lng),
      )

      if (!isExploreItem(marker.item) && isRouteItem(marker.item)) {
        bounds.extend(
          new google.maps.LatLng(
            marker.item.endPlace.point.lat,
            marker.item.endPlace.point.lng,
          ),
        )
      }
    })

    map.setOptions({
      minZoom: DEFAULT_MAP_OPTIONS.minZoom,
      maxZoom: DEFAULT_MAP_OPTIONS.maxZoom,
      center: undefined,
      zoom: undefined,
    })

    if (bounds.isEmpty() && selectedPlace) {
      bounds.extend(
        new google.maps.LatLng(
          selectedPlace?.point.lat ?? 0,
          selectedPlace?.point.lng,
        ),
      )
    }

    map.fitBounds(bounds, FIT_BOUNDS_PADDING)
    google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
      map.setOptions({ minZoom: USER_ZOOM.min, maxZoom: USER_ZOOM.max })
    })
  } else if (map && selectedPlace && markers.length === 0) {
    map.setOptions({
      minZoom: DEFAULT_MAP_OPTIONS.minZoom,
      maxZoom: DEFAULT_MAP_OPTIONS.maxZoom,
      center: undefined,
      zoom: undefined,
    })

    const bounds = new google.maps.LatLngBounds(
      new google.maps.LatLng(
        selectedPlace.point.lat ?? 0,
        selectedPlace.point.lng,
      ),
    )

    map.fitBounds(bounds, FIT_BOUNDS_PADDING)
    google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
      map.setOptions({ minZoom: USER_ZOOM.min, maxZoom: USER_ZOOM.max })
    })
  }
}

export function filterMapElements<T extends TripItemMapMarker | ExploreItemMapMarker | TripItemMapRoute>(
  trip: FullTrip | PublicFullTrip,
  markers: Array<T>,
  layerGroups: Array<MapLayerGroup>,
  filters: Array<App.TripPlannerSubFilterType> = [],
): Array<T> {
  let filteredMarkers = [...markers]

  if (filters.length > 0) {
    const filterSet = new Set(filters)
    filteredMarkers = filteredMarkers.filter(
      (marker) => isExploreMarker(marker) ? true : filterProducts(marker.item, filterSet),
    )
  }

  const layerGroupsById = arrayToObject(layerGroups, group => group.id)
  const itineraryLayers = layerGroupsById.itinerary?.layers ?? []
  const enabledItineraryLayerIds = new Set(itineraryLayers.filter((layer) => layer.isSelected).map((layer) => layer.id))
  if (itineraryLayers.length > 0) {
    filteredMarkers = filteredMarkers.filter((marker) => {
      if (marker.mapElementType === 'exploreItemMarker' ||
        (marker.mapElementType === 'tripItemMarker' &&
          ('icon' in marker.item || marker.item.type === 'ACCOMMODATION' || marker.item.type === 'EXPERIENCE'))) {
        return true
      }
      const itinerarySection = itemIsInItinerary(marker.item, trip) ? marker.item.startDate! : NO_DATES
      return enabledItineraryLayerIds.has(itinerarySectionToString(itinerarySection))
    })
  }

  return filteredMarkers
}

export const findNewOrMovedTripItems = (
  currentUserId: string | undefined,
  newItems: Array<TripItem | PublicTripItem>,
  prevItems: Array<TripItem | PublicTripItem>,
  minUpdateTime: moment.Moment,
) => {
  const prevItemsMap = arrayToMap(prevItems, item => item.id)

  return (
    newItems
      // Find all the items that have changed by the current user
      .filter((newItem) => {
        if (!('updatedBy' in newItem) || newItem.updatedBy !== currentUserId || newItem.updatedAt.isBefore(minUpdateTime)) {
          return false
        }
        const prevItem = prevItemsMap.get(newItem.id)

        if (!prevItem) {
          return true
        }

        const prevPlace = getItemPlace(prevItem, 'start')
        const newPlace = getItemPlace(newItem, 'start')
        const prevPoint = prevPlace?.type === 'GEO' ? prevPlace.point : undefined
        const newPoint = newPlace?.type === 'GEO' ? newPlace.point : undefined

        return newPoint && (
          prevPoint?.lat !== newPoint.lat ||
          prevPoint?.lng !== newPoint.lng
        )
      })
      .sort((a, b) => {
        if ('updatedAt' in a && 'updatedAt' in b) {
          return b.updatedAt.unix() - a.updatedAt.unix()
        }
        return 0
      })
  )
}

export const isPinnedItem = (
  item: MapItem | PublicMapItem,
): item is MarkerItem =>
  ((item.type === 'ATTRACTION' ||
    item.type === 'EVENT' ||
    item.type === 'EXPERIENCE' ||
    item.type === 'ACCOMMODATION' ||
    item.type === 'RESTAURANT_BAR') &&
    item.place.type === 'GEO') ||
    ((item.type === 'CAR_RENTAL' ||
      item.type === 'CRUISE' ||
      item.type === 'TOUR' ||
      item.type === 'THING_TO_DO') &&
      item.startPlace.type === 'GEO')

export const isRouteItem = (item: MapItem | PublicMapItem): item is RouteItem =>
  (item.type === 'FLIGHT' ||
    item.type === 'BUS' ||
    item.type === 'FERRY' ||
    item.type === 'TRAIN' ||
    item.type === 'OTHER_TRANSPORT') &&
    item.startPlace.type === 'GEO' &&
    item.endPlace.type === 'GEO'

export const getPathBounds = (
  startPoint: google.maps.LatLngLiteral,
  endPoint: google.maps.LatLngLiteral,
) => {
  const southWestPoint: google.maps.LatLngLiteral = {
    lat: Math.min(startPoint.lat, endPoint.lat),
    lng: Math.min(startPoint.lng, endPoint.lng),
  }
  const northEastPoint: google.maps.LatLngLiteral = {
    lat: Math.max(startPoint.lat, endPoint.lat),
    lng: Math.max(startPoint.lng, endPoint.lng),
  }

  return new google.maps.LatLngBounds(southWestPoint, northEastPoint)
}

// Aligns bottom of object with lat/lng
export const pinOffSetter = (offsetWidth: number, offsetHeight: number) => ({
  x: -offsetWidth / 2,
  y: -offsetHeight + 3, // Google adds 3px of spacing from OverlayView border
})

// Centers object at lat/lng
export const centeredOffSetter = (
  offsetWidth: number,
  offsetHeight: number,
) => ({
  x: -offsetWidth / 2,
  y: -offsetHeight / 2,
})

export function isMapItem<T extends { type: TripItem['type'] }>(
  item: T,
): item is Exclude<T, { type: 'NOTE' }> {
  return item.type !== 'NOTE'
}

function addPixelOffsetToCoords(
  projection: google.maps.MapCanvasProjection,
  latLng: GeoPoint,
  offset: { x?: number, y?: number },
) {
  const point = projection.fromLatLngToContainerPixel(latLng)
  if (!point) {
    return null
  }
  const newPoint = new google.maps.Point(
    point.x + (offset.x ?? 0),
    point.y + (offset.y ?? 0),
  )
  return projection.fromContainerPixelToLatLng(newPoint)
}

// Thresholds where the pin is considered too close to the edge of the map
// and should be panned to the centre if selected.
const VERTICAL_PIN_MARGIN = 32
const HORIZONTAL_PIN_MARGIN = 16

export function panToIntoViewIfObstructed(
  map: google.maps.Map,
  position: GeoPoint,
  container?: HTMLElement | null,
) {
  const overlay = new google.maps.OverlayView()
  overlay.draw = () => {}
  overlay.setMap(map)
  const projection = overlay.getProjection()

  if (projection) {
    const pixelPos = projection.fromLatLngToContainerPixel(position)
    const carouselHeight = container?.offsetHeight ?? 0
    const mapHeight = map.getDiv().offsetHeight
    const mapWidth = map.getDiv().offsetWidth

    if (
      pixelPos &&
      (pixelPos.y > mapHeight - carouselHeight - VERTICAL_PIN_MARGIN ||
        pixelPos.y < VERTICAL_PIN_MARGIN ||
        pixelPos.x > mapWidth - HORIZONTAL_PIN_MARGIN ||
        pixelPos.x < HORIZONTAL_PIN_MARGIN)
    ) {
      const panToCoords = addPixelOffsetToCoords(projection, position, {
        y: carouselHeight / 2,
      })
      if (panToCoords) {
        map.panTo(panToCoords)
      }
    }
  }
}

export function startPlaceOfTripItem(item: TripItem): GeoPlace | undefined {
  if ('startPlace' in item && 'id' in item.startPlace) {
    return item.startPlace
  }
  if ('place' in item && 'id' in item.place) {
    return item.place
  }
  return undefined
}
